import * as React from 'react';
import {
  getButtonWrapperStyle,
  getSlidesStyle,
  getSlideStyle,
  preventDoubleClick,
  getAdditionalOffset,
  computeNewOffset,
} from './utils';
import { IDesktopSliderProps, IDesktopSliderState, IScrollToSlideDesktopParams } from './types';
import * as styles from './DesktopSlider.css';
import { ISlider } from '../types';
import { Button } from './components/Button';
import { mergeStyles } from '@cian/utils';
import { getSafeSlide } from '../utils';

export class DesktopSlider extends React.Component<IDesktopSliderProps, IDesktopSliderState> implements ISlider {
  public static defaultProps = {
    visibleSlides: 1,
    step: 1,
    initialSlide: 0,
    additionalOffset: 0,
    animationTime: 0.5,
    indents: {},
  };

  /* индекс первого видимого слайда */
  private currentSlide = 0;
  private overflowContainerRef = React.createRef<HTMLDivElement>();
  private slidesRef = React.createRef<HTMLDivElement>();

  public constructor(props: IDesktopSliderProps) {
    super(props);

    const { visibleSlides, children } = this.props;

    this.state = {
      offset: 0,
      enableAnimation: false,
      isMinPositionReached: true,
      isMaxPositionReached: visibleSlides >= children.length,
    };
  }

  public componentDidMount() {
    const { initialSlide } = this.props;

    this.scrollToSlide(initialSlide, { enableAnimation: false });
  }

  public scrollToSlide = (candidateSlide: number, params: IScrollToSlideDesktopParams = {}) => {
    const { enableAnimation = true, onEndScroll } = params;
    const { indents } = this.props;

    const nextSlide = getSafeSlide({ slide: candidateSlide, lastSlide: this.lastSlide });

    if (nextSlide === this.currentSlide) {
      return;
    }

    this.currentSlide = nextSlide;

    const overflowContainerNode = this.overflowContainerRef.current;
    const nextSlideNode = this.getSlideNode(nextSlide);

    if (!overflowContainerNode || !nextSlideNode) {
      return;
    }

    const additionalOffset = getAdditionalOffset({
      slide: nextSlide,
      lastSlide: this.lastSlide,
      additionalOffset: this.props.additionalOffset,
      indents,
    });

    /**
     * Вычисление getBoundingClientRect для overflowContainer внутри scrollToSlide
     * дает возможность ресайзить страницу без потери точности расчетов слайдера.
     * При возникновении проблемы перформанса - сохранять размеры контейнеров при маунте
     * и добавить слушателя на ресайз.
     */
    const newOffset = computeNewOffset({ overflowContainerNode, slideNode: nextSlideNode, additionalOffset });

    this.setState(
      ({ offset }) => ({
        offset: offset + newOffset,
        enableAnimation,
        isMinPositionReached: nextSlide === 0,
        isMaxPositionReached: nextSlide === this.lastSlide,
      }),
      onEndScroll,
    );
  };

  private getSlideNode = (currentSlide: number): null | HTMLElement => {
    const slidesNode = this.slidesRef.current;

    if (!slidesNode) {
      return null;
    }

    const slideNode = slidesNode.children[currentSlide] as HTMLElement | void;

    return slideNode || null;
  };

  private get lastSlide() {
    const { visibleSlides, children } = this.props;

    return children.length - visibleSlides;
  }

  private handleSlideClick = (index: number) => () => {
    const { visibleSlides, onScrollLeft, onScrollRight } = this.props;

    const isScrollToLeft = index < this.currentSlide;
    const isScrollToRight = index > this.currentSlide + visibleSlides - 1;

    if (isScrollToLeft) {
      this.scrollToSlide(this.currentSlide - 1, { onEndScroll: onScrollLeft });
    }

    if (isScrollToRight) {
      this.scrollToSlide(this.currentSlide + 1, { onEndScroll: onScrollRight });
    }
  };

  private handleLeftButtonClick = preventDoubleClick(() => {
    const { step, onScrollLeft } = this.props;

    this.scrollToSlide(this.currentSlide - step, { onEndScroll: onScrollLeft });
  }, this.props.animationTime);

  private handleRightButtonClick = preventDoubleClick(() => {
    const { step, onScrollRight } = this.props;

    this.scrollToSlide(this.currentSlide + step, { onEndScroll: onScrollRight });
  }, this.props.animationTime);

  public render() {
    const { offset, isMaxPositionReached, isMinPositionReached, enableAnimation } = this.state;
    const { children, animationTime, indents } = this.props;

    const slidesStyle = getSlidesStyle({ animationTime, enableAnimation, indents, offset });
    const buttonWrapperStyle = getButtonWrapperStyle({ indents });

    return (
      <div className={styles['relative-container']}>
        <div className={styles['overflow-container']} ref={this.overflowContainerRef}>
          <div ref={this.slidesRef} className={styles['slides']} style={slidesStyle}>
            {React.Children.map(children, (child, index) => {
              const isLastSlide = index === children.length - 1;
              const slideStyle = getSlideStyle({ isLastSlide, indents });

              return (
                // TODO: добавить memoize для onClick
                <div
                  style={slideStyle}
                  className={styles['slide']}
                  onClick={this.handleSlideClick(index)}
                  onTouchEnd={this.handleSlideClick(index)}
                >
                  {child}
                </div>
              );
            })}
          </div>
        </div>

        {!isMinPositionReached && (
          <div {...mergeStyles(styles['button-wrapper'], styles['left'], buttonWrapperStyle)}>
            <Button direction="left" onClick={this.handleLeftButtonClick} />
          </div>
        )}

        {!isMaxPositionReached && (
          <div {...mergeStyles(styles['button-wrapper'], styles['right'], buttonWrapperStyle)}>
            <Button direction="right" onClick={this.handleRightButtonClick} />
          </div>
        )}
      </div>
    );
  }
}
